import dealService from 'resources/deal/deal.service';
import templateService from 'resources/template/template.service';
import * as companyApi from 'resources/company/company.api';
import taskService from 'resources/task/task.service';
import { isEmailTemplate } from 'resources/task/task.helpers';
import { ADVANCED_FIELDS_TYPES } from 'components/transaction/advancedFields/index.helpers';


export default async function updateDeal(updateData) {
  const oldDeal = await dealService.findById(updateData.id);
  const updatedDeal = await dealService.updateDeal(updateData);

  const updatedTemplates = await tryFillInEmailAddresses(oldDeal, updateData);
  if (updatedTemplates?.length) {
    updatedDeal.updatedTemplateIds = updatedTemplates.map(template => template.id);
  }

  return updatedDeal;
}

// This function works only if there are existing email templates, which To, CC or BCC fields
// have Labels instead of actual emails, e.g. 'Escrow Officer Email' instead of 'escrow@example.com'.
// So when the deal is updated with the actual email address 'escrow@example.com', we replace the Label
// 'Escrow Officer Email' with 'escrow@example.com' in all email templates that had the Label before.
const tryFillInEmailAddresses = async (oldDeal, updateData) => {
  const updatedEmailFields = await getUpdatedEmailFields(oldDeal, updateData);
  if (!updatedEmailFields || updatedEmailFields.length === 0) return;

  const dealEmailTemplates = await getDealEmailTemplates(oldDeal);
  if (!dealEmailTemplates?.length) return;

  const updatePromises = [];
  dealEmailTemplates.forEach(template => {
    const toEmailsReplaced = replaceLabelsWithEmails(template.to_emails, updatedEmailFields);
    const ccEmailsReplaced = replaceLabelsWithEmails(template.cc_emails, updatedEmailFields);
    const bccEmailsReplaced = replaceLabelsWithEmails(template.bcc_emails, updatedEmailFields);

    if (toEmailsReplaced || ccEmailsReplaced || bccEmailsReplaced) {
      updatePromises.push(templateService.updateTemplate({
        id: template.id,
        ...(toEmailsReplaced && { to_emails: template.to_emails }),
        ...(ccEmailsReplaced && { cc_emails: template.cc_emails }),
        ...(bccEmailsReplaced && { bcc_emails: template.bcc_emails }),
      }));
    }
  });

  const updatedTemplates = await Promise.all(updatePromises);
  return updatedTemplates;
}

// Get all updated Email advanced fields
const getUpdatedEmailFields = async (oldDeal, updateData) => {
  const { advanced_fields } = updateData;
  if (!advanced_fields) return;

  const updatedFields = Object.keys(advanced_fields)
    .filter(field => oldDeal.advanced_fields[field] !== advanced_fields[field])
    .map(key => ({ name: key, value: advanced_fields[key] }));
  if (updatedFields.length === 0) return;

  const companyAdvancedFields = await companyApi.getAdvancedFields(oldDeal.company_id);

  const updatedEmailFields = updatedFields
    .map(field => {
      const companyField = companyAdvancedFields.find(cf => cf.name === field.name);
      return (companyField && companyField.type === ADVANCED_FIELDS_TYPES.EMAIL)
        ? { name: field.name, value: field.value, label: companyField.label }
        : null;
    })
    .filter(Boolean);

  return updatedEmailFields;
}

// Get all email templates related to the deal
const getDealEmailTemplates = async (oldDeal) => {
  const dealTasks = await taskService.getTasksByDealId(oldDeal.id);
  const dealEmails = (dealTasks || []).filter(task => isEmailTemplate(task));
  const dealEmailTemplateIds = dealEmails.map(dealEmail => dealEmail.template_id);
  if (dealEmailTemplateIds.length === 0) return;

  const dealEmailTemplates = await templateService.getByIds(oldDeal.company_id, dealEmailTemplateIds);
  return dealEmailTemplates;
}

const replaceLabelsWithEmails = (emails, updatedEmailFields) => {
  let wereReplaced = false;

  if (emails) {
    for (const i = 0; i < emails.length; i++) {
      const email = emails[i];
      const updatedEmail = updatedEmailFields.find(field => field.label === email);
      if (updatedEmail) {
        wereReplaced = true;
        emails[i] = updatedEmail.value;
      }
    }
  }

  return wereReplaced;
}